'use strict';

var AppDispatcher = require('../core/AppDispatcher.jsx');
var EventEmitter = require('events').EventEmitter;
var assign = require('object-assign');
var Constants = require('../constants/YourDetailsConstants');
var Values = Constants.Values;
var ApplicationConstants = require('../constants/ApplicationConstants');
var SelectCustomerConstants = require('../constants/SelectCustomerConstants');
var FormKeys = ApplicationConstants.FormKeys;
var ProductIds = ApplicationConstants.ProductIds;
var Products = require('../utils/Products');
var AccountTypes = require('../utils/AccountTypes');
var DateTime = require('../utils/DateTime');
var YourDetailsHelpers = require('../utils/YourDetailsHelpers');
var ApplicationHelper = require('../utils/ApplicationHelper');
var newInvestor = YourDetailsHelpers.createNewInvestor;
var YourDetailsRetrieve = require('../utils/YourDetailsRetrieve');
var YourDetailsSave = require('../utils/YourDetailsSave');
var Mapper = require('../utils/Mapper.js');
var _ = require('lodash');
var AddressActionTypes = require('../constants/AddressConstants.js').ActionTypes;
var FundingYourProductConstants = require('../constants/FundingYourProductConstants.js');
var SectionNames = FundingYourProductConstants.SectionNames;
var ActionTypes = Constants.ActionTypes;
var ApplicationActionTypes = ApplicationConstants.ActionTypes;
var AccountTypeActionsTypes = require('../constants/AccountTypeConstants').ActionTypes;
var SelectCustomerActionsTypes = require('../constants/SelectCustomerConstants.js').ActionTypes;
var CHANGE_EVENT = 'change';
var Validators = require('../utils/Validators.js');
var YourDetailsValidations = require('../utils/YourDetailsValidations.js').default;
var ClientEditabilityRules = require('../utils/ClientEditabilityRules.js').default;
var ContentService = require('../services/ContentService.js');
var PensionTypeConstants = require('../constants/PensionTypeConstants.js');
var BeneficiaryDetailsConstants = require('../constants/BeneficiaryDetailsConstant.js');
var AccountTypeCodes = ApplicationConstants.AccountTypeCodes;
var Properties = Constants.Properties;

var _applicationData, _applicationLinkType;
var _accountType;
var _productId, _clientOin = 0, _client;
var _formKey;
var _titleList = [];
var _errorList = [];
var _validationSummary = [];
var _countryList = [], _tfnExemptionList = [], _investorList = [], _trusteeList = [];
var _investorLimitReached = false;
var isFirstLoad = true;
var hasDobBeenUpdated = false;
var _customAttributeName;
var formData = {};
var _summaryFromFunding = {};
var _crossSectionDependencyMsg = [];
var _ripModel = {};
var _eligibleContributionsFromRip = {};
var _selectedPensionType;
var _selectedPensionTypeOptions = _.cloneDeep(PensionTypeConstants.PensionTypeSelection);
var _showRipSection = true;
const _soleTraderIdPrefix = 'ydSoleTraderBusiness';
var _firstNetClient = false;
var _hasCustomTitle = false;
const _existingCustomerReadonlyFields = Constants.ExistingCustomerReadonlyFields;
var _sourceOfFundList = [];
var _sourceOfWealthList = [];

function applyEditabilityRulesForIressApplications() {
    // If the KYC flag from Commsee is set to 'Yes' for CFP-advised customers, the name, address and DOB fields will also need to be read only.
    // This only applies to individual type of customers (Name (full name, surname), residential address and date of birth)
    // from: https://confluence.prod.cba/display/FAM/XPLAN+prepopulation
    if (!_client ||
        !_client.kycFlag ||
        _client.kycSource !== ApplicationConstants.KycSources.COMMSEE ||
        !_applicationData ||
        !ApplicationHelper.isCfpAdvised(_applicationData) ||
        _client.customerType !== ApplicationConstants.ClientTypes.INDIVIDUAL ||
        !YourDetailsHelpers.isIndividualApplication(_productId, _accountType)) {
        return;
    }

    // find the main applicant
    var mainApplicant;
    if (_accountType === ApplicationConstants.AccountTypeCodes.JOINT) {
        if (_investorList && _investorList.length >= 1) {
            // first joint member is the main applicant
            mainApplicant = _investorList[0]
        }
    } else {
        mainApplicant = formData;
    }

    // apply editablity rules
    if (mainApplicant) {
        var rules = ClientEditabilityRules.getRulesForCfpAdvisedWithCommSeeKyc(_productId);
        mainApplicant = _.merge(mainApplicant, rules);
    }
}

function prePopulateClientWithExisting(entity, isTrusteeCorporate) {
    if (!entity || !_client) {
        return entity;
    }
    //if existing client is a non-indidvual and the product is super/pension or account type is individual/joint no prepop should happen
    //so the new individual should not have an oin
    if (_client.customerType === ApplicationConstants.ClientTypes.BUSINESS &&
        YourDetailsHelpers.isIndividualApplication(_productId, _accountType)) {
        entity.oin = undefined;
        return entity;
    }

    // prepopulate client
    entity = _.merge(entity, _client);

    // apply editablity rules
    var rules = ClientEditabilityRules.getRules(_accountType, _productId, _client, isTrusteeCorporate);
    entity = _.merge(entity, rules);

    if (isTrusteeCorporate) {
        entity.corporateTrustee = _client.businessName;
        entity.oin = _client.oin;
    }

    //if existing client is a non individual and the account type is either company or partnership the oin should be passed to the entity not the first investor
    if (_client.customerType === ApplicationConstants.ClientTypes.BUSINESS && (_accountType === AccountTypeCodes.COMPANY || _accountType === AccountTypeCodes.PARTNERSHIP)) {
        entity.oin = undefined;
    }

    // For Sole Trader, we only prepopulate the residential address, as business address as per requirement
    if (_accountType === AccountTypeCodes.SOLE_TRADER) {
        if (_client.customerType === ApplicationConstants.ClientTypes.BUSINESS) {
            entity.businessName = entity.lastName;
            entity.businessAddress = _.clone(entity.addressInfo);
            entity.addressInfo = _.clone(Properties.AddressInfo);
            entity.gender = null;
            entity.title = null;
            entity.givenName = null;
            entity.lastName = null;
            entity.occupation = null;
        } else if (_client.customerType === ApplicationConstants.ClientTypes.INDIVIDUAL) {
            entity.businessName = null;
        }
    }

    entity.isOtherCountryTaxResident = null;
    entity.otherCountryTaxResidence1 = null;

    return entity;
}

function yourDetailsStoreInit() {
    formData = {
        productId: _productId,
        oin: _clientOin,
        cif: undefined,
        gender: undefined,
        title: undefined,
        givenName: undefined,
        lastName: undefined,
        dateOfBirth: undefined,
        addressInfo: _.clone(Properties.AddressInfo),
        mobileNumber: undefined,
        homeNumber: undefined,
        workNumber: undefined,
        emailAddress: undefined,
        tfnNumber: undefined,
        tfnExemptionReason: undefined,
        hasTfnExemption: undefined,
        tfnWithConsent: undefined,
        tfnConsentFlag: undefined,
        isAustralianResident: undefined,
        countryOfResidence: undefined,
        isOtherCountryTaxResident: undefined,
        otherCountryTaxResidence1: undefined,
        otherCountryTaxResidence2: undefined,
        otherCountryTaxResidence3: undefined,
        isCitizenOfOtherCountry: undefined,
        citizenship1: undefined,
        citizenship2: undefined,
        tin1: undefined,
        tin2: undefined,
        tin3: undefined,
        tinExemptionReason1: undefined,
        tinExemptionReason2: undefined,
        tinExemptionReason3: undefined,
        businessName: undefined,
        businessAddress: _.clone(Properties.AddressInfo),
        abn: undefined,
        occupation: undefined,
        isPowerOfAttorney: false,
        isAccountDesignation: undefined,
        accountDesignationName: undefined,
        titleReadonly: undefined,
        givenNameReadonly: undefined,
        lastNameReadonly: undefined,
        genderReadonly: undefined,
        dateOfBirthReadonly: undefined,
        occupationReadonly: undefined,
        countryOfResidenceReadonly: undefined,
        tfnNumberReadonly: undefined,
        mobileNumberReadonly: undefined,
        homeNumberReadonly: undefined,
        workNumberReadonly: undefined,
        emailAddressReadonly: undefined,
        isAustralianResidentReadonly: undefined,
        isOtherCountryTaxResidentReadonly: undefined,
        otherCountryTaxResidenceReadonly: undefined,
        isCitizenOfOtherCountryReadonly: undefined,
        citizenship1Readonly: undefined,
        citizenship2Readonly: undefined,
        businessNameReadonly: undefined,
        abnReadonly: undefined,
        withExistingMobileNumber: undefined,
        kycFlag: undefined,
        kycType: undefined,
        isAustralianTaxResident: undefined,
        sourceOfFund: undefined,
        sourceOfWealth: undefined,
        isOtherSourceOfFund: undefined,
        otherSourceOfFund: undefined,
        isOtherSourceOfWealth: undefined,
        otherSourceOfWealth: undefined,
        sourceOfFundDescription: undefined,
        sourceOfWealthDescription: undefined
    };

    // pre-populate formData with existing client data if any
    formData = prePopulateClientWithExisting(formData);

    _formKey = Products.getFormKey(_accountType, _productId);
    _customAttributeName = YourDetailsValidations.getCustomAttributeNames(_formKey);
    _errorList = [];
    _validationSummary = [];
    _investorList = [];
    _trusteeList = [];
    _investorLimitReached = false;

    if (!(_formKey === ProductIds.PENSION || _formKey === ProductIds.SUPER || _formKey === FormKeys.PARTNERSHIP_INVESTMENT)) {
        formData.hasTfnExemption = formData.hasTfnExemption || '';
    }

    if (_.indexOf([ProductIds.PENSION, ProductIds.SUPER, FormKeys.SOLE_TRADER_INVESTMENT, FormKeys.INDIVIDUAL_INVESTMENT, FormKeys.JOINT_INVESTMENT], _formKey) === -1) {
        delete formData.isPowerOfAttorney;
    }
    else {
        if (formData.isPowerOfAttorney === undefined) {
            formData.isPowerOfAttorney = false;
        }
    }

    if (Products.isInvestment(_productId) || Products.isAnnuity(_productId)) {
        if (_accountType === ApplicationConstants.AccountTypeCodes.SOLE_TRADER || _accountType === ApplicationConstants.AccountTypeCodes.INDIVIDUAL) {
            formData.isAccountDesignation = ApplicationConstants.YesNoValues.NO;
        }
    }
}

function setClient(client) {
    _client = client;
    _firstNetClient = _client.source === ApplicationConstants.ClientSources.FMS;
    _hasCustomTitle = _client.title === ApplicationConstants.CustomTitle;
    if (_applicationData &&
        _applicationData.originationSource === ApplicationConstants.OriginationSources.IRESS &&
        _applicationLinkType === SelectCustomerConstants.ApplicationLinkTypes.EXISTING_LISTED_CLIENT) {
        // existing client in FMS takes precedence over XPLAN but cif, kycFlag should retain 
        if (ApplicationHelper.isCfpAdvised(_applicationData)) {
            _client = _.assign(client,
            {
                cif: _.get(formData, 'cif', client.cif),
                kycFlag: _.get(formData, 'kycFlag', client.kycFlag),
                kycSource: _.get(formData, 'kycSource', client.kycSource),
                kycType: _.get(formData, 'kycType', client.kycType)
            });
        }

        formData = prePopulateClientWithExisting(formData);
    }
    applyEditabilityRulesForIressApplications();
}

function setClientOin(clientOin) {
    _clientOin = clientOin;
    formData.oin = clientOin;
}

function getValidationRules(index, fieldId) {
    var record = getCurrentRecord(index);
    var isExistingCustomer = index === 0 && _firstNetClient;
    if (record) {
        var rules;
        //SMSF/TRUST will use _trusteeList and they would have trusteeType of Individual or Corporate
        if (index !== undefined && record.trusteeType !== undefined) {
            rules = YourDetailsValidations.getTrusteeRules(_formKey, record.trusteeType, record, _productId, isExistingCustomer, _hasCustomTitle);
            return YourDetailsValidations.getValidationRules(fieldId, rules);
        }
            //for single investor account types we can directly use the _firstNetClient flag to check if this is for an existing client
        else if (_formKey === FormKeys.SOLE_TRADER_INVESTMENT || _formKey === FormKeys.INDIVIDUAL_INVESTMENT || Products.isSuper(_productId) || Products.isPension(_productId)) {
            rules = YourDetailsValidations.getRules(_formKey, _firstNetClient, _hasCustomTitle, record);
            rules = YourDetailsValidations.validationRulesInit(rules, record, _productId);
            return YourDetailsValidations.getValidationRules(fieldId, rules);
            //for multiple investor account types we only apply the special rules for the existing client not the new ones.
        } else {
            rules = YourDetailsValidations.getRules(_formKey, isExistingCustomer, _hasCustomTitle, record);
            rules = YourDetailsValidations.validationRulesInit(rules, record, _productId);
            return YourDetailsValidations.getValidationRules(fieldId, rules);
        }
    }
    else {
        return;
    }
}

function validateTrusteeType(index, multipleSectionId) {
    var record = {
        trusteeType: undefined
    }
    var rules = {
        trusteeType: 'required'
    }
    var id = `ydTrustee{x}${index}`;
    var inlineErrorList = Validators.validateAll(record, rules, _customAttributeName, id);
    var summaryErrorList = Validators.validateAll(record, rules, _customAttributeName, id, 'Your details', multipleSectionId);

    return { inlineErrorList: inlineErrorList, summaryErrorList: summaryErrorList };
}

function validateField(fieldName, fieldId, fieldValue, index, addressInfo, addressCustomAttr, isBusiness) {
    var rules = (!addressInfo) ? getValidationRules(index, fieldName) : YourDetailsValidations.getAddressValidationRules(addressInfo, fieldName, null, isBusiness);

    if (!rules) {
        Validators.clearErrorListForField(_errorList, fieldId);
        return;
    }
    else {
        var data = {};
        data[fieldName] = fieldValue;
        Validators.validateField(data, rules, addressCustomAttr || _customAttributeName, _errorList, fieldName, fieldId);
    }
}

function validateForSave() {
    if (_productId === ProductIds.PENSION || _productId === ProductIds.SUPER || _accountType) {
        var record = getRecord();
        var inlineErrorList = [], summaryErrorList = [], errors;
        if (record && record.constructor === Array) {
            var multipleId = Products.getInvestorTypeMessageId(_accountType);
            // for save, we only validate the first item
            if (record[0]) {
                errors = validateRecordForSave(record[0], 0, multipleId + 1);
                inlineErrorList = inlineErrorList.concat(errors.inlineErrorList);
                summaryErrorList = summaryErrorList.concat(errors.summaryErrorList);
            }
        }
        else if (record) {
            errors = validateRecordForSave(record);
            inlineErrorList = errors.inlineErrorList;
            summaryErrorList = errors.summaryErrorList;
        }
        _errorList = inlineErrorList;
        _validationSummary = summaryErrorList;
    }
}

function validateRecordForSave(record, index, multipleSectionId) {
    var rules = YourDetailsValidations.getRulesForSave(_formKey);
    var customAttributeName = YourDetailsValidations.getCustomAttributeNames(_formKey, true);
    var id;
    if (index === parseInt(index)) {
        id = record.sectionName + '{x}' + index;
    }
    var inlineErrorList = Validators.validateAll(record, rules, customAttributeName, id);
    var summaryErrorList = Validators.validateAll(record, rules, customAttributeName, id, 'Your details', multipleSectionId);
    return { inlineErrorList: inlineErrorList, summaryErrorList: summaryErrorList };
}

function validateAll() {
    //Validate only if your details is shown
    if (_productId === ProductIds.PENSION || _productId === ProductIds.SUPER || _accountType) {
        var record = getRecord();
        var inlineErrorList = [], summaryErrorList = [], errors;
        if (record && record.constructor === Array) {
            var multipleId = Products.getInvestorTypeMessageId(_accountType);
            _.times(record.length, function (index) {
                if (record[index]) {
                    errors = validate(record[index], index, multipleId + getInvestorNumber(index));
                    inlineErrorList = inlineErrorList.concat(errors.inlineErrorList);
                    summaryErrorList = summaryErrorList.concat(errors.summaryErrorList);
                }
                else {
                    errors = validateTrusteeType(index, multipleId + getInvestorNumber(index));
                    inlineErrorList = inlineErrorList.concat(errors.inlineErrorList);
                    summaryErrorList = summaryErrorList.concat(errors.summaryErrorList);
                }
            });
        }
        else if (!ApplicationHelper.isBusinessAccountType(_accountType)) {
            errors = validate(record);
            inlineErrorList = errors.inlineErrorList;
            summaryErrorList = errors.summaryErrorList;
        }
        _errorList = inlineErrorList;
        _validationSummary = summaryErrorList;
    }
}

function getInvestorNumber(index) {
    if (index !== undefined) {
        return index + 1;
    }
}

function validate(record, index, multipleSectionId) {
    let isExistingCustomer = (index === 0 || index === undefined) && _firstNetClient;
    let rules = getValidationRules(index);
    //CEE-2: Bypassing field validations for existing customer if its readonly.
    if (isExistingCustomer) {
        _existingCustomerReadonlyFields.forEach(rule => {
            if (record[rule]) {
                delete rules[rule.replace('Readonly', '')];
            }
        });
    }
    var id;
    if (index === parseInt(index)) {
        id = record.sectionName + '{x}' + index;
    }
    var inlineErrorList = Validators.validateAll(record, rules, _customAttributeName, id);
    var summaryErrorList = Validators.validateAll(record, rules, _customAttributeName, id, 'Your details', multipleSectionId);

    summaryErrorList = validateSummaryErrorForMobileAndEmail(summaryErrorList);

    inlineErrorList = inlineErrorList.concat(validateAddress(id, index));
    summaryErrorList = insertAddressValidationSummary(record, rules, summaryErrorList, validateAddress(id, index, true, multipleSectionId));

    if (_formKey === FormKeys.SOLE_TRADER_INVESTMENT) {

        inlineErrorList = inlineErrorList.concat(validateSoleTraderAddressPrefixId(id, index, false));

        var soleTraderSummary = validateSoleTraderAddressPrefixId(id, index, true);

        summaryErrorList = insertAddressValidationSummary(record, rules, summaryErrorList, soleTraderSummary);
    }

    return { inlineErrorList: inlineErrorList, summaryErrorList: summaryErrorList };
}

//Displaying mobileNumberOrEmailRequired validation only once if it appears twice in summary error list
function validateSummaryErrorForMobileAndEmail(summaryErrorList){
    var MobileOrEmailErrorMessage = 'Either mobile number or email address is required';
    var count =0;
    if(summaryErrorList) {
        for (var error of summaryErrorList ) {
            if(error.message.includes(MobileOrEmailErrorMessage)) {
              count ++; 
              // Check if mobileNumberOrEmailRequired is present twice
                if(count == 2) {
                   summaryErrorList.splice(summaryErrorList.indexOf(error),1);
                   count = 0;
                }
            }
        }
    }
    return summaryErrorList;
}

function validateSoleTraderAddressPrefixId(id, index, summary) {
    var result = validateAddress(id, index, summary, '', true);

    _.forEach(result, function (n) {
        n.id = _soleTraderIdPrefix + n.id;
    });

    return result;
}

//So that summary errors are in order, we need to insert address errors.
//Depending on the form, address is inserted after specific field.
function insertAddressValidationSummary(record, rule, summaryErrorList, addressSummary) {
    var field = 'dateOfBirth';
    switch (_formKey) {
        case FormKeys.TRUST_INVESTMENT:
        case FormKeys.SMSF_INVESTMENT:
            if (record.trusteeType === Constants.TrusteeType.CORPORATE) {
                field = 'abn';
            }
            break;
        case FormKeys.SMSF_COMMINSURE:
        case FormKeys.TRUST_COMMINSURE:
        case FormKeys.SMSF_CHALLENGER:
        case FormKeys.TRUST_CHALLENGER:
            field = 'occupation';
            break;
        case FormKeys.SOLE_TRADER_INVESTMENT:
            var soleTraderPrefixId = _.filter(addressSummary, function (o) { return _.startsWith(o.id, _soleTraderIdPrefix) })

            if (soleTraderPrefixId.length > 0) {
                field = 'businessName';
            }
            break;
        default:
            break;
    }

    var secondHalf = summaryErrorList.splice(YourDetailsHelpers.getIndexForInsert(rule, field, summaryErrorList));
    var errorList = summaryErrorList.concat(addressSummary, secondHalf);
    return errorList;
}

function validateAddressField(fieldName, fieldId, fieldValue, index, isBusiness) {
    var currentRecord = getCurrentRecord(index);
    if (currentRecord) {
        var addressInfo = getAddressData(currentRecord, isBusiness);
        var isSoleTrader = _.startsWith(fieldId, _soleTraderIdPrefix);
        validateField(fieldName, fieldId, fieldValue, index, addressInfo, getAddressCustomAttribute(currentRecord, addressInfo, isSoleTrader), isBusiness || currentRecord.trusteeType === Constants.TrusteeType.CORPORATE);
        removeAustraliaStateValidations(fieldName, fieldValue);
    }
}

function removeAustraliaStateValidations(fieldName) {
    if (fieldName === 'resCountry') {
        _.remove(_errorList, (o) => { return o.id.indexOf('cityStatePostCode') !== -1 });
        _.remove(_errorList, (o) => { return o.id.indexOf('resAusState') !== -1 });
        _.remove(_errorList, (o) => { return o.id.indexOf('postCode') !== -1 });
    }
    else if (fieldName === 'postalCountry') {
        _.remove(_errorList, (o) => { return o.id.indexOf('postalCityStatePostCode') !== -1 });
        _.remove(_errorList, (o) => { return o.id.indexOf('postalAusState') !== -1 });
        _.remove(_errorList, (o) => { return o.id.indexOf('postalPostCode') !== -1 });
    }
}

function removeValidation(listOfIds) {
    YourDetailsHelpers.removeValidation(_errorList, listOfIds, Validators);
}

function validateAddress(id, index, forSummary, multipleSectionId, isSoleTrader) {
    var withoutPostalFormsArray = [FormKeys.COMPANY_INVESTMENT, FormKeys.PARTNERSHIP_INVESTMENT, FormKeys.TRUST_COMMINSURE, FormKeys.TRUST_CHALLENGER, FormKeys.SMSF_COMMINSURE, FormKeys.SMSF_CHALLENGER]
    var isWithoutPostal = withoutPostalFormsArray.indexOf(_formKey) !== -1;

    var currentRecord = getCurrentRecord(index);

    var addressInfo = getAddressData(currentRecord, isSoleTrader);

    var addressRules = YourDetailsValidations.getAddressValidationRules(addressInfo, null, isWithoutPostal || isSoleTrader, isSoleTrader);

    var customAttribute = getAddressCustomAttribute(currentRecord, addressInfo, isSoleTrader);

    if (forSummary) {
        YourDetailsHelpers.addAddressNotFoundValidation(addressInfo, addressRules);
        return Validators.validateAll(addressInfo, addressRules, customAttribute, id, 'Your details', multipleSectionId);
    }
    else {
        return Validators.validateAll(addressInfo, addressRules, customAttribute, id);
    }
}

function getAddressData(currentRecord, isBusiness) {
    var addressInfo;
    if (currentRecord) {
        if (isBusiness) {
            addressInfo = currentRecord.businessAddress;
        }
        else {
            addressInfo = currentRecord.addressInfo;
        }
    }
    return addressInfo;
}

function getAddressCustomAttribute(currentRecord, addressInfo, isSoleTrader) {
    if (currentRecord && currentRecord.trusteeType === Constants.TrusteeType.CORPORATE) {
        return YourDetailsValidations.getAddressCustomAttributeNamesForBusiness(addressInfo);
    } else if (isSoleTrader) {
        return YourDetailsValidations.getAddressCustomAttributeNamesForSoleTrader(addressInfo);
    } else {
        return YourDetailsValidations.getAddressCustomAttributeNames(addressInfo);
    }
}

function setProductId(productId) {
    _productId = productId;
    // reset accoutn type
    setAccountType();
}

function setAccountType(accountType) {
    _accountType = accountType;
}

function initInvestors(isRetrieve) {
    switch (_formKey) {
        case FormKeys.INDIVIDUAL_COMMINSURE:
        case FormKeys.INDIVIDUAL_CHALLENGER:
            formData.countryOfResidence = Values.COUNTRY_AUSTRALIA;
            break;
        case FormKeys.JOINT_INVESTMENT:
        case FormKeys.JOINT_COMMINSURE:
        case FormKeys.JOINT_CHALLENGER:
        case FormKeys.COMPANY_INVESTMENT:
        case FormKeys.PARTNERSHIP_INVESTMENT:
            if (!isRetrieve) {
                addInvestor();
            }
            break;
        case FormKeys.TRUST_INVESTMENT:
        case FormKeys.SMSF_INVESTMENT:
            if (_client) {
                var isTrusteeCorporate = _client.customerType === ApplicationConstants.ClientTypes.BUSINESS;
                var trustee = newInvestor(0, Constants.InvestorType.TRUSTEE, isTrusteeCorporate);
                trustee = prePopulateClientWithExisting(trustee, isTrusteeCorporate);
                _trusteeList.push(trustee);
            } else {
                _trusteeList.push(undefined);
            }
            break;
        case FormKeys.TRUST_COMMINSURE:
        case FormKeys.SMSF_COMMINSURE:
        case FormKeys.TRUST_CHALLENGER:
        case FormKeys.SMSF_CHALLENGER:
            var initInvestors = _client && _client.customerType === ApplicationConstants.ClientTypes.INDIVIDUAL;
            addAdditionalInvestors(false, initInvestors);
            break;
        default:
            break;
    }
}

function saveAddressIsFound(id, result, model, value, index, type) {
    if (type !== BeneficiaryDetailsConstants.BENEFICARY) {
        var saveAddressFunc = (type === Constants.Values.BUSINESS && _accountType === ApplicationConstants.AccountTypeCodes.SOLE_TRADER) ? saveBusinessAddressInfo : saveAddressInfo
        saveAddressFunc(model, result, index);
        var address = 'postal';
        Validators.clearErrorListForField(_errorList, id);
        //Only residential address has PO Box validation.
        if (model === 'isResAddressFound') {
            address = 'residential';
            if (result) {
                validateAddressField(address, id, value, index);
            }
        }
        saveAddressFunc(address, value, index);
    }
}

function saveAddressInfo(fieldId, fieldValue, index) {
    var record = getCurrentRecord(index);
    if (record) {
        record.addressInfo[fieldId] = fieldValue;
        YourDetailsHelpers.postAddressInfoSave(record.addressInfo, fieldId);
    }
}

function saveBusinessAddressInfo(fieldId, fieldValue) {
    formData.businessAddress[fieldId] = fieldValue;
    YourDetailsHelpers.postAddressInfoSave(formData.businessAddress, fieldId);
}

function updateYourDetails(id, value, index) {
    var record = getCurrentRecord(index);
    if (record && id in record) {
        updateYourDetailsFieldDependency(record, id, value);
        record[id] = value;
        if (id === 'hasTfnExemption' && value === ApplicationConstants.Clear) {
            record.hasTfnExemption = ''
        }
    }
}

function updateYourDetailsFieldDependency(record, id, value) {
    switch (id) {
        case 'gender':
            record.title = undefined;
            break;
        case 'isAustralianResident':
            if (value === ApplicationConstants.YesNoValues.NO) {
                record.countryOfResidence = '';
            } else if (value === ApplicationConstants.YesNoValues.YES) {
                record.countryOfResidence = Constants.Values.COUNTRY_AUSTRALIA;
            }
            break;
        case 'isOtherCountryTaxResident':
            record.otherCountryTaxResidence1 = '';
            record.otherCountryTaxResidence2 = '';
            record.otherCountryTaxResidence3 = '';
            record.tinExemptionReason1 = '';
            record.tinExemptionReason2 = '';
            record.tinExemptionReason3 = '';
            record.tin1 = '';
            record.tin2 = '';
            record.tin3 = '';
            break;
        case 'tin1':
            record.tinExemptionReason1 = '';
            break;
        case 'tin2':
            record.tinExemptionReason2 = '';
            break;
        case 'tin3':
            record.tinExemptionReason3 = '';
            break;
        case 'tinExemptionReason1':
            record.tin1 = '';
            break;
        case 'tinExemptionReason2':
            record.tin2 = '';
            break;
        case 'tinExemptionReason3':
            record.tin3 = '';
            break;
        case 'isCitizenOfOtherCountry':
            record.citizenship1 = '';
            record.citizenship2 = '';
            break;
        case 'dateOfBirth':
            if (record.dateOfBirth !== value) {
                hasDobBeenUpdated = true;
                _crossSectionDependencyMsg = [];
            }
            break;
        case 'isAccountDesignation':
            if (value === ApplicationConstants.YesNoValues.NO) {
                record.accountDesignationName = '';
            }
            break;
        case 'tfnConsentFlag':
            if (value === ApplicationConstants.YesNoValues.YES) {
                record.tfnWithConsent = '';
            }
            break;
        case 'hasTfnExemption':
            if (record['hasTfnExemption'] !== value) {
                record.tfnExemptionReason = '';
                record.tfnConsentFlag = '';
                if (!record.tfnNumberReadonly) {
                    record.tfnNumber = '';
                }
                record.tfnWithConsent = '';
            }
            break;
        case 'sourceOfFund':
            if (value !== undefined && value.includes(ApplicationConstants.SofSowOther.SOFOT)) {
                record.isOtherSourceOfFund = true;
            }
            else {
                record.isOtherSourceOfFund = false;
            }
            break;
        case 'sourceOfWealth':
            if (value !== undefined && value.includes(ApplicationConstants.SofSowOther.SOWOT)) {
                record.isOtherSourceOfWealth = true;
            }
            else {
                record.isOtherSourceOfWealth = false;
            }
            break;
        default:
            break;
    }
}

function getAge() {
    var dob = formData.dateOfBirth;
    if (dob) {
        return DateTime.getAge(new Date(), dob);
    }
}

function getCurrentRecord(index) {
    var currentRecord = getRecord(index);
    if (currentRecord && currentRecord.constructor === Array) {
        return null;
    }
    return currentRecord;
}

function getRecord(index) {
    var record = null;
    switch (_formKey) {
        case FormKeys.JOINT_INVESTMENT:
        case FormKeys.JOINT_CHALLENGER:
        case FormKeys.JOINT_COMMINSURE:
        case FormKeys.COMPANY_INVESTMENT:
        case FormKeys.PARTNERSHIP_INVESTMENT:
            record = (index === parseInt(index)) ? _investorList[index] : _investorList;
            break;
        case FormKeys.SMSF_INVESTMENT:
        case FormKeys.TRUST_INVESTMENT:
        case FormKeys.SMSF_COMMINSURE:
        case FormKeys.TRUST_COMMINSURE:
        case FormKeys.SMSF_CHALLENGER:
        case FormKeys.TRUST_CHALLENGER:
            record = (index === parseInt(index)) ? _trusteeList[index] : _trusteeList;
            break;
        default:
            record = formData;
            break;
    }
    return record;
}

function retrieveYourDetails(yourDetails) {
    YourDetailsRetrieve.retrieveYourDetails(yourDetails, formData);
    YourDetailsRetrieve.retrieveInvestorList(yourDetails, _investorList);
    YourDetailsRetrieve.retrieveTrusteeList(yourDetails, _trusteeList);
    if (_investorList.length > 0) {
        investorLimitHasReached(_investorList);
    }
    else if (_trusteeList.length > 0) {
        investorLimitHasReached(_trusteeList);
    }
}

function addInvestor() {
    var investor;
    if (_investorList.length == 0) {
        switch (_formKey) {
            case FormKeys.JOINT_INVESTMENT:
            case FormKeys.JOINT_CHALLENGER:
            case FormKeys.JOINT_COMMINSURE:
                //for joint investments, there are two investors yourDetailsStoreInitially displayed
                investor = newInvestor(0, Constants.InvestorType.INVESTOR);
                investor.oin = _clientOin;
                if (_formKey === FormKeys.JOINT_INVESTMENT) {
                    investor.isPowerOfAttorney = false; // add isPowerOfAttorney for first investor under JOINT_INVESTMENT
                }
                investor.isAccountDesignation = ApplicationConstants.YesNoValues.NO;
                investor.accountDesignationName = null;
                // pre-populate the first joint investor with retrieved client data if any
                investor = prePopulateClientWithExisting(investor);
                _investorList.push(investor);
                _investorList.push(newInvestor(1, Constants.InvestorType.INVESTOR));
                break;
            case FormKeys.COMPANY_INVESTMENT:
            case FormKeys.PARTNERSHIP_INVESTMENT:
                //for company and partnership investment, one investor is required
                investor = newInvestor(0, Constants.InvestorType.INVESTOR);
                if (_client && _client.customerType === ApplicationConstants.ClientTypes.INDIVIDUAL) {
                    investor = prePopulateClientWithExisting(investor);
                }
                investor.oin = _clientOin;
                _investorList.push(investor);
                break;
            default:
                break;
        }
    }
}

function addNewObjects(list, investorType, isCorporate, initInvestors) {
    var investorLimit = AccountTypes.getInvestorLimit(_formKey);
    if (list.length < investorLimit) {
        if (_formKey === FormKeys.TRUST_INVESTMENT || _formKey === FormKeys.SMSF_INVESTMENT) {
            list.push(undefined);
        }
        else {
            var investor = newInvestor(list.length, investorType, isCorporate);
            if (initInvestors) {
                investor = prePopulateClientWithExisting(investor);
            }
            list.push(investor);
        }
        investorLimitHasReached(list);
    }
    else {
        _investorLimitReached = false;
    }
}

function investorLimitHasReached(list) {
    var investorLimit = AccountTypes.getInvestorLimit(_formKey);
    if (list.length === investorLimit) {
        _investorLimitReached = true;
    } else {
        _investorLimitReached = false;
    }
}

function addAdditionalInvestors(isCorporate, initInvestors) {
    switch (_formKey) {
        case FormKeys.JOINT_INVESTMENT:
        case FormKeys.JOINT_CHALLENGER:
        case FormKeys.JOINT_COMMINSURE:
        case FormKeys.COMPANY_INVESTMENT:
        case FormKeys.PARTNERSHIP_INVESTMENT:
            addNewObjects(_investorList, Constants.InvestorType.INVESTOR, false);
            break;
        case FormKeys.TRUST_CHALLENGER:
        case FormKeys.TRUST_COMMINSURE:
        case FormKeys.SMSF_CHALLENGER:
        case FormKeys.SMSF_COMMINSURE:
        case FormKeys.TRUST_INVESTMENT:
        case FormKeys.SMSF_INVESTMENT:
            addNewObjects(_trusteeList, Constants.InvestorType.TRUSTEE, isCorporate, initInvestors);
            break;
        default:
            break;
    }

}

function addTrustee(isCorporate, index) {
    _.remove(_errorList, function (o) { return o.id.slice(-1) === index.toString() });
    _trusteeList[index] = newInvestor((_trusteeList[index] !== undefined) ? index : _trusteeList.length, Constants.InvestorType.TRUSTEE, isCorporate);
}

function deleteTrustee(index) {
    var list = [], idPrefix = '';
    if (index < _trusteeList.length) {
        list = _trusteeList;
        idPrefix = 'ydTrustee';
    } else if (index < _investorList.length) {
        list = _investorList;
        idPrefix = 'ydInvestor';
    }

    list.splice(index, 1);
    removeTrusteeFromErrorList(index, idPrefix, list);
    _investorLimitReached = false;
}

function removeTrusteeFromErrorList(index, idPrefix, list) {
    var isTrusteeErrror = function (error, index) {
        return error.id.match('^' + idPrefix) && error.id.match(index + '$');
    }
    _.remove(_errorList, function (error) { return isTrusteeErrror(error, index); });
    for (var i = index + 1; i < list.length + 1; i++) {
        var newError = _.filter(_errorList, function (error) { return isTrusteeErrror(error, i); });
        var newIndex = i - 1;
        var oldIndexPattern = new RegExp(i + '$');
        _.forEach(newError, function (error) {
            error.id = error.id.replace(oldIndexPattern, newIndex);
        });
    }
}

function setSummaryFromFunding(summary) {
    if (!_.isEqual(summary, _summaryFromFunding)) {
        if (!_.isEmpty(_summaryFromFunding)) {
            if (_.findIndex(_summaryFromFunding, 'rollOverType', FundingYourProductConstants.FundingTypes.Spouse_contribution) > -1 && _.findIndex(summary, 'rollOverType', FundingYourProductConstants.FundingTypes.Spouse_contribution) < 0 || _.findIndex(_summaryFromFunding, 'rollOverType', FundingYourProductConstants.FundingTypes.Personal_contribution) > -1 && _.findIndex(summary, 'rollOverType', FundingYourProductConstants.FundingTypes.Personal_contribution) < 0) {
                if (hasDobBeenUpdated) {
                    _.remove(_crossSectionDependencyMsg, (o) => { return o.content === Mapper.getObjectByAttribute(SectionNames, _productId, 'id').label; });
                    _crossSectionDependencyMsg.push({ content: Mapper.getObjectByAttribute(SectionNames, _productId, 'id').label, order: 2 });
                }
            }
        }
    }

    hasDobBeenUpdated = false;
    _summaryFromFunding = _.cloneDeep(summary);
}

function setRipModel(ripModel) {
    if (!_.isEqual(ripModel, _ripModel)) {
        if (!_.isEmpty(_ripModel)) {
            if (_ripModel.ripOption === ApplicationConstants.YesNoValues.YES && !ripModel.ripOption) {
                _.remove(_crossSectionDependencyMsg, (o) => { return o.content === ContentService.getContent().crossSectionHeaders.regularInvestmentPlan; });
                _crossSectionDependencyMsg.push({ content: ContentService.getContent().crossSectionHeaders.regularInvestmentPlan, order: 3 });
            }
        }
    }

    _ripModel = _.cloneDeep(ripModel);
}

function setRipEligibleContribution(contributions) {
    if (!_.isEqual(contributions, _eligibleContributionsFromRip)) {
        if (!_.isEmpty(_eligibleContributionsFromRip) && !_.isEmpty(_ripModel)) {
            if (_ripModel.ripOption === ApplicationConstants.YesNoValues.YES) {
                _.remove(_crossSectionDependencyMsg, (o) => { return o.content === ContentService.getContent().crossSectionHeaders.regularInvestmentPlan; });
                _crossSectionDependencyMsg.push({ content: ContentService.getContent().crossSectionHeaders.regularInvestmentPlan, order: 3 });
            }
        }
    }

    _eligibleContributionsFromRip = _.cloneDeep(contributions);
}

function setSelectedPensionType(pensionType) {
    if (pensionType !== _selectedPensionType) {
        if (_selectedPensionType && formData.dateOfBirth && !pensionType) {
            _.remove(_crossSectionDependencyMsg, (o) => { return o.content === ContentService.getContent().crossSectionHeaders.pensionType; });
            _crossSectionDependencyMsg.push({ content: ContentService.getContent().crossSectionHeaders.pensionType, order: 1 });
        }
    }

    _selectedPensionType = pensionType;
}

function setSelectedPensionTypeOptions(pensionTypeOptions) {
    if (!_.isEqual(pensionTypeOptions, _selectedPensionTypeOptions)) {
        _.remove(_crossSectionDependencyMsg, (o) => { return o.content === ContentService.getContent().crossSectionHeaders.pensionType; });
        _crossSectionDependencyMsg.push({ content: ContentService.getContent().crossSectionHeaders.pensionType, order: 1 });
    }

    _selectedPensionTypeOptions = pensionTypeOptions;
}

function sortCrossSectionOrder() {
    _crossSectionDependencyMsg = _.sortBy(_crossSectionDependencyMsg, 'order');
}

function receivedAddress(form) {
    if (form.residentialAddress && form.residentialAddress.address) {
        formData.addressInfo.isResAddressFound = true;
        formData.addressInfo.residential = form.residentialAddress.address;
    }
    if (form.postalAddress && form.postalAddress.address && form.postalAddress.isSameAsResidentialAddress === ApplicationConstants.YesNoValues.YES) {
        formData.addressInfo.postal = '';
    }
}

function setShowRipSection(showRipSection) {
    if (showRipSection !== _showRipSection) {
        _.remove(_crossSectionDependencyMsg, (o) => { return o.content === ContentService.getContent().crossSectionHeaders.regularInvestmentPlan; });
        _crossSectionDependencyMsg.push({ content: ContentService.getContent().crossSectionHeaders.regularInvestmentPlan, order: 3 });
    }

    _showRipSection = showRipSection;
}

var YourDetailsStore = assign({}, EventEmitter.prototype, {
    emitChange: function () {
        this.emit(CHANGE_EVENT);
    },

    /**
     * @param {function} callback
     */
    addChangeListener: function (callback) {
        this.on(CHANGE_EVENT, callback);
    },

    /**
     * @param {function} callback
     */
    removeChangeListener: function (callback) {
        this.removeListener(CHANGE_EVENT, callback);
    },

    getTitleList: function () {
        return _titleList;
    },

    getCountryList: function () {
        return _countryList;
    },

    getFormKey: function () {
        return _formKey;
    },

    getTfnExemptionList: function () {
        return _tfnExemptionList;
    },

    getAddressInfo: function (index) {
        var info = '';
        if (index === 0 || index > 0) {
            info = _investorList[index].addressInfo;
        }
        else {
            info = formData.addressInfo;
        }
        return info;
    },

    getAccountType: function () {
        return _accountType;
    },

    getIsAgeLessThan60: function () {
        var age = getAge();
        if (age && age < 60) {
            return true;
        }
        else {
            return false;
        }
    },

    getErrorList: function () {
        return _errorList;
    },

    getInvestorList: function () {
        return _investorList;
    },

    getCurrentInvestor: function (id) {
        return _investorList[id];
    },

    getInvestorCount: function () {
        return _investorList.length;
    },

    getInvestorLimitReached: function () {
        return _investorLimitReached;
    },

    getTrusteeList: function () {
        return _trusteeList;
    },

    getCurrentTrustee: function (id) {
        return _trusteeList[id];
    },

    getIsNextTrusteeCorporate: function (id) {
        var testValue = id + 1;
        if (_trusteeList.length == 0 || _trusteeList.length == testValue) {
            return false;
        } else {
            return _trusteeList[testValue].trusteeType == Constants.TrusteeType.CORPORATE;
        }

    },

    getYourDetailsData: function () {
        return formData;
    },

    getYourDetailsModel: function () {
        return YourDetailsSave.save(this);
    },

    getValidationSummary: function () {
        return _validationSummary;
    },

    getBusinessAddressInfo: function () {
        return formData.businessAddress;
    },

    getAge: function () {
        return getAge();
    },

    getCrossSectionDependencyMsg: function () {
        sortCrossSectionOrder();
        return _crossSectionDependencyMsg;
    },

    getFirstNetClient: function () {
        return _firstNetClient;
    },

    getHasCustomTitle: function () {
        return _hasCustomTitle;
    },
    getSourceOfFundList: function () {
        return _sourceOfFundList;
    },
    getSourceOfWealthList: function () {
        return _sourceOfWealthList;
    }
});

YourDetailsStore.dispatchToken = AppDispatcher.register(function (action) {
    if (isFirstLoad) {
        YourDetailsStore.setMaxListeners(0);
        isFirstLoad = false;
    }

    switch (action.actionType) {
        case ApplicationActionTypes.NEW_APPLICATION_INITIALISED:
            setProductId(action.productId);
            yourDetailsStoreInit();
            break;
        case ApplicationActionTypes.APPLICATION_RETRIEVE_SUCCESS:
            _applicationData = action.applicationData;
            setProductId(action.applicationData.productId);
            setAccountType(action.applicationData.form.accountType);
            yourDetailsStoreInit();
            if (action.applicationData.form.yourDetails) {
                retrieveYourDetails(action.applicationData.form.yourDetails);
                receivedAddress(action.applicationData.form.yourDetails);
            }
            _hasCustomTitle = action.applicationData.hasCustomTitle;
            _firstNetClient = action.applicationData.isFirstNetClient;
            break;
        case ApplicationActionTypes.VALIDATE_APPLICATION:
            validateAll();
            break;
        case ApplicationActionTypes.APPLICATION_SAVE_VALIDATE:
            if (!ApplicationHelper.isBusinessAccountType(_accountType)) {
                validateForSave();
            }
            break;
        case AccountTypeActionsTypes.SELECT_ACCOUNT_TYPE:
            setAccountType(action.accountType);
            yourDetailsStoreInit();
            initInvestors();
            break;
        case ActionTypes.UPDATE_YOUR_DETAILS:
            updateYourDetails(action.fieldId, action.fieldValue, action.index);
            validateField(action.fieldId, action.componentId, action.fieldValue, action.index);
            break;
        case ApplicationActionTypes.LOAD_TITLE_LIST:
            _titleList = action.titles;
            break;
        case ApplicationActionTypes.LOAD_COUNTRY_LIST:
            _countryList = action.countryList;
            break;
        case ApplicationActionTypes.LOAD_TFN_EXEMPTION_LIST:
            _tfnExemptionList = action.tfnExemptionList ? action.tfnExemptionList : [];
            break;
        case ActionTypes.SAVE_ADDRESS_INFO:
            saveAddressInfo(action.fieldId, action.fieldValue, action.index);
            validateAddressField(action.fieldId, action.componentId, action.fieldValue, action.index);
            break;
        case ActionTypes.SAVE_BUSINESS_ADDRESS:
            if (action.index === undefined || action.index === '') {
                saveBusinessAddressInfo(action.fieldId, action.fieldValue);
                validateAddressField(action.fieldId, action.componentId, action.fieldValue, action.index, true);
            }
            else {
                saveAddressInfo(action.fieldId, action.fieldValue, action.index);
                validateAddressField(action.fieldId, action.componentId, action.fieldValue, action.index);
            }
            break;
        case ActionTypes.ADD_ADDITIONAL_INVESTORS:
            addAdditionalInvestors(action.isCorporate);
            break;
        case ActionTypes.YOUR_DETAILS_REMOVE_VALIDATION:
            removeValidation(action.listOfIds);
            break;
        case ActionTypes.ADD_TRUSTEE:
            addTrustee(action.isCorporate, action.index);
            var field = 'trusteeType';
            validateField(field, 'ydTrustee' + field + action.index, null, action.index);
            break;
        case ActionTypes.DELETE_TRUSTEE:
            deleteTrustee(action.index);
            break;
        case AddressActionTypes.RECEIVED_VALIDATION_RESULT:
            saveAddressIsFound(action.id, action.result, action.model, action.value, action.index, action.addressType);
            break;
        case SelectCustomerActionsTypes.SET_CLIENT_OIN:
            setClientOin(action.clientOin);
            break;
        case ApplicationConstants.ActionTypes.IS_FUNDING_SUMMARY:
            setSummaryFromFunding(action.summary);
            break;
        case SelectCustomerActionsTypes.CLIENT_RETRIEVE_COMPLETED:
            setClient(action.client);
            break;
        case ApplicationActionTypes.SEND_RIP_MODEL:
            setRipModel(action.model);
            break;
        case ApplicationActionTypes.SEND_ELIGIBLE_CONTRIBUTION_FROM_RIP:
            setRipEligibleContribution(action.contributions);
            break;
        case ApplicationActionTypes.IS_PRE_RETIREMENT:
            setSelectedPensionType(action.value);
            break;
        case ApplicationActionTypes.SEND_PENSION_TYPE_OPTIONS:
            setSelectedPensionTypeOptions(action.pensionTypeOptions);
            break;
        case ApplicationActionTypes.SEND_SHOW_RIP_SECTION:
            setShowRipSection(action.showRipSection);
            break;
        case ApplicationActionTypes.SET_APPLICATION_LINK_TYPE:
            _applicationLinkType = action.applicationLinkType;
            break;
        case ApplicationActionTypes.LOAD_SOURCEOFFUND_LIST:
            _sourceOfFundList = action.sourceOfFundList ? action.sourceOfFundList : [];
            break;
        case ApplicationActionTypes.LOAD_SOURCEOFWEALTH_LIST:
             _sourceOfWealthList = action.sourceOfWealthList ? action.sourceOfWealthList : [];
             break;
        default:
            return true;
    }

    // If action was responded to, emit change event
    YourDetailsStore.emitChange();
    return true;

});

yourDetailsStoreInit();

module.exports = YourDetailsStore;



// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./stores/YourDetailsStore.js